import { useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { inbox, contacts, broadcasts, billing, channelWa, flows } from '@repletoryreach/sdk-api';
import { formatNumber } from '@repletoryreach/sdk-utils/format';
import { formatRelative } from '@repletoryreach/sdk-utils/date';
import { Stack, Card, Group, Text, SimpleGrid, Button, Badge, Avatar, ActionIcon, FitText } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { Sparkline } from '@repletoryreach/sdk-ui/charts';
import { useCredits } from '../../lib/credits';
import { shownBalance } from '../../lib/wallet';
import { useT } from '../../i18n/I18nProvider';
import { useI18n } from '../../i18n/I18nProvider';
import { SetupChecklist } from '../../app/components/SetupChecklist';
import { formatCount } from '../../lib/format';
import './dashboard.css';
import { useWallet } from '../../data/useWallet';
import { HelpButton } from '../../app/guides/HelpButton';

/** Rows fetched for the recent list. Previously 8 were fetched and 6 rendered. */
const RECENT_LIMIT = 6;

export default function DashboardPage() {
  const { user } = useAuth();
  const t = useT();
  const credits = useCredits();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();

  /*
   * Query keys are shared with the pages that own this data (and with
   * SetupChecklist), so TanStack serves one request instead of the two
   * duplicate pairs this page used to issue under `['dash', …]`
   * (_UI_improvements/tenant/dashboard.md §4 item 10).
   */
  const convs = useQuery({
    queryKey: ['conversations', 'recent', RECENT_LIMIT],
    queryFn: () => inbox.listConversations({ limit: RECENT_LIMIT })
  });
  // Two count queries (`['contacts', 'count']`, `['broadcasts', 'count']`) were fetched here and never read; the
  // summaries below carry the figures (4 October 2026).
  const walletQ = useWallet();

  /*
   * W47: the four `…/summary` endpoints. Before these existed the page had no
   * source for a movement figure at all, and its "unread" hint was the sum of
   * `unread_count` over whichever six rows it happened to have fetched, dressed
   * up as an org-wide total (§4 item 2).
   */
  const convSummary = useQuery({
    queryKey: ['conversations', 'summary'],
    queryFn: () => inbox.getConversationSummary(),
    select: (r) => r.data
  });
  const contactSummary = useQuery({
    queryKey: ['contacts', 'summary'],
    queryFn: () => contacts.getContactSummary(),
    select: (r) => r.data
  });
  const broadcastSummary = useQuery({
    queryKey: ['broadcasts', 'summary'],
    queryFn: () => broadcasts.getBroadcastSummary(),
    select: (r) => r.data
  });
  const deliverySummary = useQuery({
    queryKey: ['messages', 'summary'],
    queryFn: () => channelWa.getMessageSummary(),
    select: (r) => r.data
  });
  const ledgerQ = useQuery({
    queryKey: ['ledger', 'recent'],
    queryFn: () => billing.listLedger({ limit: 100 })
  });

  /*
   * Whether anything can send at all. It rides the delivery-rate query the
   * page already makes, so the commonest case (nothing is wrong) costs no
   * extra request and renders nothing.
   */
  const sendBlock = deliverySummary.data?.send_block ?? null;

  // Things that need a person, said on the dashboard (3 October 2026): a number WhatsApp disconnected because it is
  // also on the WhatsApp Business app, and a website in the knowledge base that could not be read or has new pages.
  const numbersQ = useQuery({ queryKey: ['phone-numbers'], queryFn: () => channelWa.listPhoneNumbers(), staleTime: 60_000 });
  const websitesQ = useQuery({ queryKey: ['kb-websites'], queryFn: () => flows.listKbWebsites(), staleTime: 60_000, retry: false });
  const blockedPayer = sendBlock ? (numbersQ.data?.data ?? []).find((n) => n.id === sendBlock.phone_number_id)?.meta_billing_payer : undefined;
  const disconnected = (numbersQ.data?.data ?? []).filter((n) => (n as channelWa.PhoneNumber).is_coexistence && (n as channelWa.PhoneNumber).disconnected_at) as channelWa.PhoneNumber[];
  const sites = websitesQ.data?.data ?? [];
  const failingSites = sites.filter((w) => w.status === 'failing');
  const newPages = sites.reduce((sum, w) => sum + (w.job ? 0 : w.analysis.new_pages ?? 0), 0);
  // Documents that disagree, so the bot could give the wrong one (3 October 2026).
  const conflictsQ = useQuery({ queryKey: ['kb-conflicts', 'dashboard'], queryFn: () => flows.listKbConflicts('open'), staleTime: 60_000, retry: false });
  const openConflicts = conflictsQ.data?.data?.length ?? 0;

  const wallet = walletQ.data?.data;
  const convList = convs.data?.data ?? [];

  /** Seven-day spend, so the balance card answers "will this last the week?". */
  const spend = useMemo(() => spend7d(ledgerQ.data?.data ?? []), [ledgerQ.data]);

  const convTrend = useTrend(convSummary.data);
  const contactTrend = useTrend(contactSummary.data);
  const broadcastTrend = useTrend(broadcastSummary.data);
  /*
   * The delivery-rate card was fetched and never drawn (4 October 2026). The broadcasts summary's rate covers
   * WhatsApp, SMS and email; channel-wa's figure (WhatsApp only, with its own trend) is the fallback when no
   * broadcast went out in the window.
   */
  const broadcastRate = broadcastSummary.data?.delivered_rate ?? null;
  const waRate = deliverySummary.data?.value ?? null;
  const deliveryRate = broadcastRate ?? waRate;
  const deliveryTrend = useTrend(broadcastRate == null ? deliverySummary.data : undefined, {});
  const deliveryQuery = broadcastRate != null ? broadcastSummary : deliverySummary;

  /*
   * Unread was summed over the 8 rows this page happened to fetch and presented
   * as though it were an org-wide figure (§4 item 2). Until the API exposes an
   * unread filter there is no honest total, so the hint now describes what the
   * card actually is: open conversations.
   */
  const anyError = [convSummary, contactSummary, broadcastSummary, walletQ, deliverySummary].some((q) => q.isError);
  const orgName = user?.org.name ?? '';
  const greeting = greetingName(user?.display_name, user?.email);

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
        <PageHeader
          withDivider={false}
          /* "Welcome, " with nothing after the comma is worse than a plain
             greeting, so a name we cannot work out drops the clause entirely. */
          title={greeting
            ? t('dashboard.title', { name: greeting })
            : t('dashboard.title.anonymous')}
          description={t('dashboard.subtitle', { org: orgName })}
        />
        <Group gap="xs" align="center">
          <HelpButton guide="dashboard" />
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('dashboard.lastUpdated', { when: formatRelative(convs.dataUpdatedAt || Date.now(), new Date(), locale) })}
          </Text>
          <Tooltip label={t('dashboard.refresh')} position="bottom">
            <ActionIcon
              variant="default"
              aria-label={t('dashboard.refresh')}
              onClick={() => { void qc.invalidateQueries(); }}
            >
              <RefreshIcon />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* A failed query used to render as a confident zero. Say so instead. */}
      {anyError && <Alert color="error" role="alert">{t('dashboard.loadError')}</Alert>}

      {/*
        Nothing is getting through.

        The delivery-rate card cannot say this: 0% reads the same whether one
        recipient blocked the business or the customer's whole WhatsApp
        Business Account has no payment method on it and Meta is turning down
        every message it accepts. Only the second is an outage, it will not
        end on its own, and the only person who can end it is the one reading
        this page. So it goes above the metrics, not inside one.

        It clears itself: channel-wa drops the block the moment a send on that
        number is delivered.
      */}
      {sendBlock && (
        <Alert color="error" role="alert" title={t('dashboard.sendBlock.title')}>
          <Stack gap={6}>
            <Text role="bodySmall">
              {t(`dashboard.sendBlock.${sendBlock.code}${
                // On the business's own WhatsApp account a billing refusal is theirs to fix in Meta (5 October 2026).
                sendBlock.code === 'ACCOUNT_NOT_PAYABLE' && blockedPayer === 'customer' ? '.customer' : ''
              }`, t('dashboard.sendBlock.META_REFUSED'), {
                number: sendBlock.msisdn ?? t('numbers.noMsisdn')
              })}
            </Text>
            {sendBlock.numbers_blocked > 1 && (
              <Text role="bodySmall">{t('dashboard.sendBlock.alsoOthers', { count: sendBlock.numbers_blocked - 1 })}</Text>
            )}
            <Text role="caption" c="var(--rr-color-text-muted)">{t('dashboard.sendBlock.notCharged')}</Text>
            <Group gap="xs">
              <Button size="xs" onClick={() => navigate('/numbers')}>{t('dashboard.sendBlock.action')}</Button>
            </Group>
          </Stack>
        </Alert>
      )}

      {disconnected.map((n) => (
        <Alert key={n.id} color="error" role="alert" title={t('dashboard.disconnected.title', { number: n.msisdn ?? n.display_name ?? '' })} data-testid="dashboard-disconnected">
          <Stack gap={6}>
            <Text role="bodySmall">{t(`numbers.coexist.disconnected.${n.disconnect_reason ?? 'other'}`, t('numbers.coexist.disconnected.other'))}</Text>
            <Group gap="xs"><Button size="xs" onClick={() => navigate('/numbers')}>{t('dashboard.disconnected.action')}</Button></Group>
          </Stack>
        </Alert>
      ))}
      {failingSites.length > 0 && (
        <Alert color="warning" title={t('dashboard.websites.failingTitle')} data-testid="dashboard-website-failing">
          <Stack gap={6}>
            <Text role="bodySmall">{t('dashboard.websites.failingBody', { names: failingSites.map((w) => w.name).join(', ') })}</Text>
            <Group gap="xs"><Button size="xs" onClick={() => navigate('/knowledge')}>{t('dashboard.websites.action')}</Button></Group>
          </Stack>
        </Alert>
      )}
      {newPages > 0 && (
        <Alert color="info" title={t('dashboard.websites.newTitle')} data-testid="dashboard-website-new">
          <Group justify="space-between" wrap="wrap">
            <Text role="bodySmall">{t('dashboard.websites.newBody', { count: formatCount(newPages) })}</Text>
            <Button size="xs" variant="default" onClick={() => navigate('/knowledge')}>{t('dashboard.websites.action')}</Button>
          </Group>
        </Alert>
      )}
      {openConflicts > 0 && (
        <Alert color="warning" title={t('dashboard.conflicts.title')} data-testid="dashboard-kb-conflicts">
          <Group justify="space-between" wrap="wrap">
            <Text role="bodySmall">{openConflicts === 1 ? t('dashboard.conflicts.bodyOne') : t('dashboard.conflicts.body', { count: formatCount(openConflicts) })}</Text>
            <Button size="xs" variant="default" onClick={() => navigate('/knowledge')}>{t('dashboard.conflicts.action')}</Button>
          </Group>
        </Alert>
      )}

      <SetupChecklist />

      {/* `cols` is a responsive map: four counters at 80px each on a phone was
          the old behaviour of a bare `cols={4}` (§4 item 18). */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3, xl: 5 }} spacing="md" data-guide="dash-stats">
        <Stat
          label={t('nav.inbox')}
          /* Open conversations, not every conversation ever opened. */
          value={count(convSummary.data?.value)}
          query={convSummary}
          hint={t('dashboard.metrics.unread', { count: convSummary.data?.unread ?? 0 })}
          trend={convTrend}
          to="/inbox"
          linkLabel={t('dashboard.viewInbox')}
          icon={<InboxIcon />}
          tone="teal"
        />
        <Stat
          label={t('nav.contacts')}
          value={count(contactSummary.data?.value)}
          query={contactSummary}
          hint={t('dashboard.metrics.contactsHint')}
          trend={contactTrend}
          to="/contacts"
          linkLabel={t('dashboard.viewContacts')}
          icon={<UsersIcon />}
          tone="green"
        />
        <Stat
          label={t('nav.broadcasts')}
          /* Was `broadcastList.length`, capped at the server's page size while
             the hint claimed "total" (§4 item 1). The summary counts runs that
             actually started, so a draft is never reported as sent. */
          value={count(broadcastSummary.data?.value)}
          query={broadcastSummary}
          hint={broadcastSummary.data?.recipients_delivered != null
            ? t('dashboard.metrics.broadcastsHintDelivered', { count: formatCount(broadcastSummary.data.recipients_delivered) })
            : t('dashboard.metrics.broadcastsHint')}
          trend={broadcastTrend}
          to="/broadcasts"
          linkLabel={t('dashboard.viewBroadcasts')}
          icon={<SendIcon />}
          tone="blue"
        />
        <Stat
          label={t('dashboard.metrics.deliveryRate')}
          value={deliveryRate == null ? '–' : `${formatNumber(deliveryRate, { maximumFractionDigits: 1 })}%`}
          query={deliveryQuery}
          hint={deliveryRate == null
            ? t('dashboard.metrics.deliveryNone')
            : broadcastRate != null
              ? t('dashboard.metrics.deliveryHintBroadcasts')
              : t('dashboard.metrics.deliveryHint')}
          trend={deliveryTrend}
          to="/broadcasts/delivery"
          linkLabel={t('dashboard.viewDeliveryReport')}
          icon={<TickIcon />}
          tone="teal"
        />
        <Stat
          label={t('nav.billing')}
          // Never a negative balance: while extra use is active it is shown on Billing instead (30 October 2026).
          value={wallet ? credits(shownBalance(wallet.credits_minor, wallet.extra_use), wallet.currency) : undefined}
          query={walletQ}
          hint={spend > 0 && wallet
            ? t('dashboard.metrics.spend7d')
            : t('dashboard.metrics.walletHint')}
          sparkline={ledgerQ.data ? nonFlat(spendSeries(ledgerQ.data.data)) : null}
          to="/billing"
          linkLabel={t('dashboard.viewWallet')}
          icon={<WalletIcon />}
          tone="amber"
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Card withBorder data-guide="dash-recent">
          <Stack gap="sm">
            <Group justify="space-between">
              <Text role="body" fw={600}>{t('dashboard.recent.title')}</Text>
              <Link to="/inbox" className="rr-dash__link">{t('dashboard.recent.viewAll')}</Link>
            </Group>

            {convs.isError && <Alert color="error" role="alert">{t('dashboard.recent.error')}</Alert>}

            {convs.isPending && !convs.isError && (
              <Stack gap="xs">{[0, 1, 2, 3].map((i) => <div key={i} className="rr-dash__row-skeleton" />)}</Stack>
            )}

            {!convs.isPending && !convs.isError && convList.length === 0 && (
              <EmptyState
                title={t('dashboard.recent.empty')}
                action={
                  <Button size="xs" onClick={() => navigate('/numbers')}>
                    {t('dashboard.recent.emptyAction')}
                  </Button>
                }
              />
            )}

            {convList.map((c) => (
              /* Rows were inert: the only way through was the header link
                 (§4 item 14). */
              <Link key={c.id} to={`/inbox?conversation=${c.id}`} className="rr-dash__conv">
                <Avatar size="sm" radius="xl" className="rr-dash__avatar">
                  {initials(c.contact_display_name ?? c.contact_msisdn ?? '')}
                </Avatar>
                <span className="rr-dash__conv-text">
                  <Text role="bodySmall" fw={600} truncate>
                    {c.contact_display_name ?? c.contact_msisdn}
                  </Text>
                  <Text role="caption" c="var(--rr-color-text-muted)" truncate>
                    {c.last_message_preview ?? t('dashboard.recent.noMessages')}
                  </Text>
                </span>
                <span className="rr-dash__conv-meta">
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {c.last_message_at ? formatRelative(c.last_message_at, new Date(), locale) : ''}
                  </Text>
                  {c.unread_count > 0 && (
                    <Badge size="xs" color="orange" variant="filled">{c.unread_count}</Badge>
                  )}
                  {/* State, with a text alternative: the dot is colour alone. */}
                  <Tooltip label={t(`inbox.state.${c.state_code ?? 'open'}`, c.state_code ?? '')} position="left">
                    <span className={`rr-dash__conv-state rr-dash__conv-state--${c.state_code ?? 'open'}`}>
                      <span className="rr-visually-hidden">
                        {t(`inbox.state.${c.state_code ?? 'open'}`, c.state_code ?? '')}
                      </span>
                    </span>
                  </Tooltip>
                </span>
              </Link>
            ))}
          </Stack>
        </Card>

        <Card withBorder data-guide="dash-quick">
          <Stack gap="sm">
            <Text role="body" fw={600}>{t('dashboard.quickActions.title')}</Text>
            {/* Six equal-weight xs buttons became a scannable grid of targets. */}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <QuickAction to="/broadcasts" icon={<SendIcon />} title={t('dashboard.quickActions.newBroadcast')} hint={t('dashboard.quickActions.newBroadcast.hint')} />
              <QuickAction to="/contacts" icon={<UsersIcon />} title={t('dashboard.quickActions.addContacts')} hint={t('dashboard.quickActions.addContacts.hint')} />
              <QuickAction to="/flows" icon={<FlowIcon />} title={t('dashboard.quickActions.buildFlow')} hint={t('dashboard.quickActions.buildFlow.hint')} />
              <QuickAction to="/templates" icon={<FileIcon />} title={t('dashboard.quickActions.newTemplate')} hint={t('dashboard.quickActions.newTemplate.hint')} />
              <QuickAction to="/numbers" icon={<PhoneIcon />} title={t('dashboard.quickActions.attachNumber')} hint={t('dashboard.quickActions.attachNumber.hint')} />
              <QuickAction to="/billing/topup" icon={<WalletIcon />} title={t('dashboard.quickActions.topUp')} hint={t('dashboard.quickActions.topUp.hint')} />
            </SimpleGrid>
          </Stack>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}

/* ── Stat card ───────────────────────────────────────────────────────────
   Laid out to images/dashboard.png: a tinted icon tile on the left, and a text
   column holding the label, the value, the hint and the "View …" link. All four
   sit INSIDE the card; the link used to hang below its border.

   §5 suggested adopting sdk-ui's `MetricCard` for `loading`, `demo` and
   `sparkline`. That component has no slot for a footer link and puts the icon
   inline beside the label, so the reference layout is not reachable from it.
   This keeps the behaviour the doc cared about (a skeleton while pending, and
   never a fabricated number on failure) and matches the design. */
interface StatProps {
  label: string;
  value: number | string | undefined;
  query: { isPending: boolean; isError: boolean };
  hint: string;
  /** Period-on-period movement, once both windows have resolved. */
  trend?: Trend | null;
  to: string;
  linkLabel: string;
  icon: ReactNode;
  tone: 'teal' | 'green' | 'blue' | 'amber';
  sparkline?: number[] | null;
}

function Stat({ label, value, query, hint, trend, to, linkLabel, icon, tone, sparkline }: StatProps) {
  const t = useT();
  const credits = useCredits();
  const failed = query.isError;

  return (
    <Card withBorder className={`rr-stat rr-stat--${tone}`}>
      <div className="rr-stat__body">
        <span className="rr-stat__icon" aria-hidden>{icon}</span>

        <div className="rr-stat__text">
          <span className="rr-stat__label">{label}</span>

          {query.isPending ? (
            <span className="rr-stat__skeleton" />
          ) : (
            // Never render a fabricated zero for a request that failed.
            // A balance like "R 1 234 567,89" shrinks to the card rather than
            // wrapping; a count draws at full size.
            <span className={`rr-stat__value${failed ? ' is-unavailable' : ''}`}>
              {failed ? t('dashboard.metrics.unavailable') : <FitText>{value ?? 0}</FitText>}
            </span>
          )}

          {!failed && !query.isPending && (
            <span className="rr-stat__hint">
              {hint}
              {trend && (
                <Tooltip label={trend.tooltip} position="bottom">
                  <span className={`rr-stat__trend rr-stat__trend--${trend.direction}`}>
                    <span aria-hidden>{TREND_GLYPH[trend.direction]}</span>
                    {trend.label}
                  </span>
                </Tooltip>
              )}
            </span>
          )}

          {!failed && sparkline && sparkline.length > 1 && (
            <Sparkline data={sparkline} color="var(--rr-stat-tone)" h={26} w="100%" withArea
              className="rr-stat__spark" />
          )}

          {/* The old link's accessible name was the hint text plus an arrow
              ("in your book →"), which told a screen-reader user nothing about
              where it went (§4 item 19). */}
          <Link to={to} className="rr-stat__link">{linkLabel}</Link>
        </div>
      </div>
    </Card>
  );
}

/* ── Trends ──────────────────────────────────────────────────────────────
   The four `…/summary` endpoints return the metric over a trailing window and
   over the window before it. The percentage is derived here rather than on the
   wire so the divide-by-zero case stays a presentation decision. */
const TREND_GLYPH = { up: '\u2197', down: '\u2198', flat: '\u2192' } as const;

interface Trend {
  direction: 'up' | 'down' | 'flat';
  label: string;
  tooltip: string;
}

function useTrend(
  summary: { current?: number | null; previous?: number | null; unit?: string } | undefined,
  opts: { /** A rise is bad for some metrics, so callers say which way is good. */ inverse?: boolean } = {}
): Trend | null {
  const t = useT();
  const credits = useCredits();
  if (!summary) return null;
  const { current, previous } = summary;
  if (current === null || current === undefined || previous === null || previous === undefined) return null;

  const points = summary.unit === 'percent';
  const delta = current - previous;

  // No baseline to compare against: "up 100%" from zero is noise, not a trend.
  if (!points && previous === 0) return null;

  const magnitude = points ? Math.abs(delta) : Math.abs(delta / previous) * 100;
  if (magnitude < 0.5) {
    return { direction: 'flat', label: t('dashboard.trend.flat'), tooltip: t('dashboard.trend.flat.tooltip') };
  }

  const rising = delta > 0;
  const good = opts.inverse ? !rising : rising;
  const label = points
    ? t('dashboard.trend.points', { value: magnitude.toFixed(1) })
    : t('dashboard.trend.percent', { value: magnitude.toFixed(0) });

  return {
    direction: good ? 'up' : 'down',
    label,
    tooltip: t('dashboard.trend.tooltip', { current: String(current), previous: String(previous) })
  };
}

function QuickAction({ to, icon, title, hint }: { to: string; icon: ReactNode; title: string; hint: string }) {
  return (
    <Link to={to} className="rr-dash__action">
      <span className="rr-dash__action-icon">{icon}</span>
      <span className="rr-dash__action-text">
        <strong>{title}</strong>
        <span>{hint}</span>
      </span>
      <ChevronIcon />
    </Link>
  );
}

/**
 * A name to greet someone by.
 *
 * `display_name` is often unset, and the fallback used to be the raw email, so
 * the dashboard opened with "Welcome, demo@repletoryreach.local". The local
 * part of an address is a far better guess at a human name, and when even that
 * looks like an identifier the greeting drops the name rather than reciting a
 * login.
 */
function greetingName(displayName?: string | null, email?: string | null): string {
  const shown = displayName?.trim();
  // Signup seeds `display_name` with the email address, so it cannot simply be
  // trusted as a human name: "Welcome, demo@repletoryreach.local" is exactly
  // the raw-identifier problem this greeting is meant to avoid.
  if (shown && !shown.includes('@')) return shown.split(/\s+/)[0];

  const local = email?.split('@')[0] ?? '';
  // "j.smith" / "john_smith" / "john-smith" are names; "u9f2c1" and "admin+7"
  // are not.
  const cleaned = local.replace(/[._-]+/g, ' ').replace(/\+.*$/, '').trim();
  const looksLikeAName = /^[a-z]{2,}( [a-z]+)*$/i.test(cleaned);
  if (!looksLikeAName) return '';

  const first = cleaned.split(' ')[0];
  return first.charAt(0).toUpperCase() + first.slice(1);
}

/** Grouped for the locale, and left undefined while the query is pending. */
function count(value: number | null | undefined): string | undefined {
  return value === null || value === undefined ? undefined : formatCount(value);
}

/** Debits over the last seven days, in minor units. */
type LedgerRow = Awaited<ReturnType<typeof billing.listLedger>>['data'][number];

function spend7d(rows: LedgerRow[]): number {
  const cutoff = Date.now() - 7 * 86400000;
  return rows
    .filter((r) => r.entry_type === 'debit' && new Date(r.created_at).getTime() >= cutoff)
    .reduce((sum, r) => sum + Math.abs(r.amount_minor ?? 0), 0);
}

/** A series of identical values draws a straight rule that reads as a trend. */
function nonFlat(series: number[]): number[] | null {
  return series.some((v) => v !== series[0]) ? series : null;
}

/** Daily debit totals, oldest first, for the wallet sparkline. */
function spendSeries(rows: LedgerRow[]): number[] {
  const days = new Map<string, number>();
  const cutoff = Date.now() - 13 * 86400000;
  for (let i = 13; i >= 0; i--) {
    days.set(new Date(Date.now() - i * 86400000).toISOString().slice(0, 10), 0);
  }
  for (const r of rows) {
    if (r.entry_type !== 'debit') continue;
    const ts = new Date(r.created_at).getTime();
    if (ts < cutoff) continue;
    const key = new Date(ts).toISOString().slice(0, 10);
    if (days.has(key)) days.set(key, (days.get(key) ?? 0) + Math.abs(r.amount_minor ?? 0));
  }
  return [...days.values()];
}

/**
 * Initials for the avatar. A contact with no name falls back to its msisdn,
 * and slicing that gives "27" for every South African number, which is worse
 * than no initials at all: use the last two digits so the avatars differ.
 */
function initials(name: string): string {
  const value = name.trim();
  if (!value) return '?';
  const digitsOnly = /^[+\d\s()-]+$/.test(value);
  if (digitsOnly) {
    const digits = value.replace(/\D/g, '');
    return digits.slice(-2) || '?';
  }
  const parts = value.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {children}
    </svg>
  );
}
function InboxIcon() { return <Icon><path d="M2.5 11.5h4l1.2 2h4.6l1.2-2h4" /><path d="M4.2 4.5h11.6l1.7 7v4.5a1 1 0 01-1 1H3.5a1 1 0 01-1-1V11.5z" /></Icon>; }
function UsersIcon() { return <Icon><circle cx="8" cy="7" r="2.8" /><path d="M2.8 16a5.2 5.2 0 0110.4 0" /><path d="M13.5 5.2a2.6 2.6 0 010 4.6M15 15.6a4.6 4.6 0 00-1.4-3.2" /></Icon>; }
function TickIcon() { return <Icon><path d="M2.5 10.5l3.2 3.2L13 6.4" /><path d="M8.5 13.2l.5.5L16.5 6.4" /></Icon>; }
function SendIcon() { return <Icon><path d="M17.5 2.5L9 11" /><path d="M17.5 2.5l-5.4 15-3.1-6.4L2.5 8z" /></Icon>; }
function WalletIcon() { return <Icon><rect x="2.5" y="5" width="15" height="10.5" rx="2" /><path d="M2.5 8.5h15" /><circle cx="14.2" cy="12" r="0.9" fill="currentColor" /></Icon>; }
function FlowIcon() { return <Icon><circle cx="5" cy="5" r="2.2" /><circle cx="15" cy="10" r="2.2" /><circle cx="5" cy="15" r="2.2" /><path d="M7.2 5h3.3a2.5 2.5 0 012.5 2.5v.3M7.2 15h3.3a2.5 2.5 0 002.5-2.5v-.3" /></Icon>; }
function FileIcon() { return <Icon><path d="M4 2.5h7l5 5v10H4z" /><path d="M11 2.5v5h5M6.5 11h7M6.5 14h5" /></Icon>; }
function PhoneIcon() { return <Icon><rect x="5.5" y="2" width="9" height="16" rx="2" /><path d="M8.8 15.2h2.4" /></Icon>; }
function ChevronIcon() {
  return (
    <svg className="rr-dash__action-chevron" width="14" height="14" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="6,3 11,8 6,13" />
    </svg>
  );
}
function RefreshIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M13.5 8a5.5 5.5 0 11-1.7-4" />
      <polyline points="13.8,1.5 13.8,4.4 10.9,4.4" />
    </svg>
  );
}
